import React, { useEffect, useMemo, useState } from 'react';
import { YMaps, Map as YMap, Placemark, Clusterer } from '@pbe/react-yandex-maps';
import { useAppSelector } from '../../store/hooks';
import './Map.less';
import { Spin } from 'antd';
import { checkIsLoading } from '../../lib/utils/checkLoadingStatus';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { Application } from '../../lib/interfaces/Application';
import { useMainContext } from '../../containers/MainContainer/MainContext';
import active_person_map from '../../lib/img/icon/person/person_active.svg';
import person_map from '../../lib/img/icon/person/person.svg';
import person_cluster_map from '../../lib/img/icon/person/person_cluster.svg';
import application_cluster_map from '../../lib/img/icon/application/application_cluster.svg';
import { Person } from '../../lib/interfaces/Person';
import { DynamicSVGReactElement } from '../common/DynamicSVGReactElement';
import moment from 'moment/moment';
import { clockStringIcon } from '../../lib/img/icon/clockString/clockString';

interface ApplicationFormat extends Application {
  iconImageOffset: number[];
  key: string;
}

interface PersonFormat extends Person {
  iconImageOffset: number[];
  key: string;
}

function MapComponent() {
  const { applicationsList, applicationsListLoading, personsList, personsListLoading } = useAppSelector(
    (state) => state.applications
  );

  const { appStatus, appSubject, personStatuses } = useAppSelector((state) => state.dictionaries);

  const { findDynamicSVGReactElement, importSVGIconStringForImg } = useMainContext();

  const formatApplicationsList = useMemo(() => {
    const result: ApplicationFormat[] = [];
    const coordMap: Map<string, number[]> = new Map();

    [...applicationsList.content].reverse().forEach((el, index) => {
      const coordsKey = el.address?.coordinates
        ? `${el.address.coordinates.point.latitude},${el.address.coordinates.point.longitude}`
        : `no-coords-${index}`;

      const offset = coordMap.get(coordsKey);
      const newOffset = offset ? [offset[0] + 5, offset[1] - 15] : [-15, 0];

      result.push({
        ...el,
        iconImageOffset: newOffset,
        key: `app-${el.id}-${index}`,
      });

      if (!offset) {
        coordMap.set(coordsKey, newOffset);
      }
    });

    return result;
  }, [applicationsList.content]);

  const formatPersonsList = useMemo(() => {
    const result: PersonFormat[] = [];
    const coordMap: Map<string, number[]> = new Map();

    [...personsList].reverse().forEach((el, index) => {
      const coordsKey = el.address?.coordinates
        ? `${el.address.coordinates.point.latitude},${el.address.coordinates.point.longitude}`
        : `no-coords-${index}`;

      const offset = coordMap.get(coordsKey);
      const newOffset = offset ? [offset[0] + 5, offset[1] - 15] : [-15, 0];

      result.push({
        ...el,
        iconImageOffset: newOffset,
        key: `person-${el.id}-${index}`,
      });

      if (!offset) {
        coordMap.set(coordsKey, newOffset);
      }
    });

    return result;
  }, [personsList]);

  const linkHeaderApplication = (path?: string, appText?: string, subjectId?: number): JSX.Element => {
    return (
      <a
        data-subjectId={subjectId}
        className="NotCustomYandexHeaderLink ml10"
        style={{ textDecoration: 'underline' }}
        href={path}
      >
        {appText}
      </a>
    );
  };

  const linkHeaderPerson = (path?: string, appText?: string, personStatusId?: number): JSX.Element => {
    return (
      <a
        data-personstatusid={personStatusId}
        className="NotCustomYandexHeaderLink YandexPersonHeader"
        style={{ textDecoration: 'underline' }}
        href={path}
      >
        {appText}
      </a>
    );
  };

  const [instanceRef, setInstanceRef] = useState<any>();
  const [instanceRefPersons, setInstanceRefPersons] = useState<any>();

  const onOpenNewTab = (path) => {
    window.open(path, '_blank');
  };

  // При клике на балун не кластера, открывать новую вкладку с просьбой
  const handleMainContentClick = (path) => () => onOpenNewTab(path);

  // При клике на балун правый кластера, открывать новую вкладку с просьбой
  const handleMainContentClusterClick = () => {
    const findHeaderNavClusterItem = document.querySelector('.ymaps-2-1-79-b-cluster-tabs__section_type_content');
    const findHeaderLinkPath = findHeaderNavClusterItem?.querySelector('a')?.pathname;
    onOpenNewTab(findHeaderLinkPath);
  };

  // Добавляем обработчик клика на балун который открывается в кластере
  const handleHeaderNavItemClick = () => {
    const findHeaderNavClusterItem = document.querySelector('.ymaps-2-1-79-b-cluster-tabs__section_type_content');

    findHeaderNavClusterItem?.removeEventListener('click', handleMainContentClusterClick);
    findHeaderNavClusterItem?.addEventListener('click', handleMainContentClusterClick);
  };

  // Добавляем обработчик клика на каждый элемент в списке меток кластера, чтобы при клике на item, вешать обработчик на весь контент балуна с права
  const addHeaderNavEventListeners = () => {
    const findHeaderNavClusterItems = document.querySelectorAll('.ymaps-2-1-79-b-cluster-tabs__menu-item');

    findHeaderNavClusterItems.forEach((headerNavItem) => {
      headerNavItem?.removeEventListener('click', handleHeaderNavItemClick);
      headerNavItem?.addEventListener('click', handleHeaderNavItemClick);
    });

    // Добавляем на уже дефолтно открытый балун справа, чтобы вешать обработчик на весь контент балуна с права
    handleHeaderNavItemClick();
  };

  // Добавляем обработчик клика на балун который открывается не в кластере
  const addMainContentEventListeners = () => {
    const findMainContent = document.querySelectorAll('.ymaps-2-1-79-balloon-content__header');

    findMainContent.forEach((mainContent) => {
      const findHeaderLink = mainContent.querySelector('a');
      const path = findHeaderLink?.pathname;

      mainContent.parentElement?.addEventListener('click', handleMainContentClick(path));
    });
  };

  useEffect(() => {
    if (instanceRef) {
      instanceRef?.events.add('balloonopen', (e) => {
        const findHeaders = document.querySelectorAll('.NotCustomYandexHeader');

        if (findHeaders.length > 0) {
          findHeaders.forEach((header) => {
            const findHeaderLink = header.querySelector('a');

            const path = findHeaderLink?.pathname;
            const appText = findHeaderLink?.innerText;
            const subjectId = Number(findHeaderLink?.dataset?.subjectid);
            const urgent = findHeaderLink?.dataset?.urgent;

            const rootHeader = createRoot(header);

            flushSync(() => {
              rootHeader.render(
                <>
                  <div
                    className="display-flex"
                    style={{
                      minWidth: urgent === 'true' ? 40 : 25,
                      maxWidth: urgent === 'true' ? 40 : 25,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {findDynamicSVGReactElement(
                      'app_subject',
                      'help',
                      'iconName',
                      subjectId,
                      urgent === 'true' ? true : false
                    )}
                  </div>

                  {linkHeaderApplication(path, appText, subjectId)}
                </>
              );
            });
          });
        }

        addHeaderNavEventListeners();
        addMainContentEventListeners();
      });
    }
  }, [instanceRef]);

  useEffect(() => {
    if (instanceRefPersons) {
      instanceRefPersons?.events.add('balloonopen', (e) => {
        const findHeaders = document.querySelectorAll('.NotCustomYandexHeader');

        if (findHeaders.length > 0) {
          findHeaders.forEach((header) => {
            const findHeaderLink = header.querySelector('a');

            const path = findHeaderLink?.pathname;
            const personText = findHeaderLink?.innerText;
            const personStatusId = Number(findHeaderLink?.dataset?.personstatusid);

            const rootHeader = createRoot(header);

            flushSync(() => {
              rootHeader.render(
                <>
                  <div
                    className="display-flex"
                    style={{ minWidth: 25, maxWidth: 25, justifyContent: 'center', alignItems: 'center' }}
                  >
                    <DynamicSVGReactElement
                      name={personStatusId === 3 ? 'person_active' : 'person'}
                      dopIcon="person"
                      dicId="person"
                    />
                  </div>

                  {linkHeaderPerson(path, personText, personStatusId)}
                </>
              );
            });
          });
        }

        addHeaderNavEventListeners();
        addMainContentEventListeners();
      });
    }
  }, [instanceRefPersons]);

  const subjectFindName = (subjectId) => appSubject.find((status) => status.id === subjectId)?.name;

  const getPointDataApplication = (app) => {
    let date = '';
    let time = '';
    if (app?.executionDate) {
      date = app?.executionDate;
    }
    if (app?.executionTime) {
      time = app?.executionTime;
    }

    const balloonDate = `
    <div>
      ${
        app?.executionDate || app.executionTime
          ? ` <div class="my-ballon__body-date-row">
                 <div class="my-ballon__body-icon">
                   ${clockStringIcon}
                 </div>
                 <div class="my-ballon__body-date-end">
                   ${date ? moment(date, 'YYYY-MM-DD').format('DD MMMM') : ''} ${time}
                 </div>
               </div>`
          : ''
      }
    </div>
  `;

    return {
      balloonContentHeader: `
      <div class="NotCustomYandexHeader cluster-items-js" style="display:flex">
        <a data-subjectId="${app.subjectId}" data-urgent="${
        app.urgent
      }" className="NotCustomYandexHeaderLink ml10" href=/applications/list/${
        app.id
      }/main style="text-decoration: underline">${subjectFindName(app?.subjectId)}</a>
      </div>
        `,

      balloonContentBody: `
       <div class="my-ballon__body">
           ${balloonDate}
           <div class="my-ballon__body-address">${app?.address.unstructuredAddress}</div>
      </div>
`,
      balloonContentFooter: `<div style="font-weight: bold; color: black">${
        appStatus.find((status) => status.id === app.statusId)?.name
      }</div>`,
      urgent: app?.urgent,
    };
  };

  const getPointDataPerson = (person: PersonFormat) => {
    const fio = `${person.lastName} ${person.firstName} ${person.middleName}`.replace('undefined', '');

    return {
      balloonContentHeader: `
      <div class="NotCustomYandexHeader" style="display:flex">
        <a data-personstatusid="${person.personStatusId}" className="NotCustomYandexHeaderLink YandexPersonHeader" href=/files/${person.id}/general style="text-decoration: underline">${fio}</a>
      </div>
        `,

      balloonContentBody: `<div class="my-ballon__body">${person?.address?.unstructuredAddress}</div>`,
      balloonContentFooter: `<div style="font-weight: bold; color: black">${
        personStatuses.find((status) => status.id === person.personStatusId)?.name
      }</div>`,
    };
  };

  const [mapRef, setMapRef] = useState<ymaps.Map>();
  const [placemarkRef, setPlacemarkRef] = useState<ymaps.Map>();

  useEffect(() => {
    if (!mapRef || !placemarkRef) {
      return;
    }

    mapRef?.events.add('click', () => {
      mapRef?.balloon.close();
    });
  }, [mapRef, placemarkRef]);

  const [applicationClusterLayout, setApplicationClusterLayout] = useState(null);

  const getApplicationClusterLayout = (ymapsInstance: any) => {
    if (!ymapsInstance?.templateLayoutFactory) return;

    const layout = ymapsInstance.templateLayoutFactory.createClass(
      `<div>
      <div class="bottom-cluster-icon"
        style="
          position: absolute;
          left: -12px; top: -12px;
          width: 34px; height: 46px;
          opacity: 1;
          background-position: 0px 0px;
          background-size: 34px 41px;
          background-image: url(${application_cluster_map});
        "
      >
      </div>
      <div class="top-cluster-icon"
        style="
          position: absolute;
          left: -14px; top: -12px;
          width: 26px; height: 40px;
          opacity: 1;
          background-position: 0px 0px;
          background-size: 26px 40px;
          background-image: url();
        "
      >
      </div>
       <div class="important-cluster-icon"
        style="
          position: absolute;
          left: -18px; top: -26px;
          width: 33px; height: 47px;
          opacity: 1;
          background-position: 0px 0px;
          background-size: 33px 47px;
          background-image: url();
        "
      >
      </div>
      </div>
      `,
      {
        build: function () {
          layout.superclass.build.call(this);

          const cluster = this.getData().geoObject;
          const geoObjects = cluster?.properties?._data?.geoObjects;

          if (!geoObjects || geoObjects.length === 0) return;

          const firstGeoObject = geoObjects[0];

          const matchSearchSubjectId =
            firstGeoObject?.properties?._data?.balloonContentHeader?.match(/data-subjectId=["']?(\d+)["']?/);
          const matchUrgent = firstGeoObject?.properties?._data?.urgent;

          if (matchSearchSubjectId) {
            const subjectId = Number(matchSearchSubjectId[1]);
            const iconUrl = importSVGIconStringForImg('app_subject', 'help_map', 'mapIconName', subjectId, matchUrgent);

            const clusterElementTop = this.getParentElement().querySelector('.top-cluster-icon');
            const clusterElementImportantTop = this.getParentElement().querySelector('.important-cluster-icon');

            if (matchUrgent) {
              if (clusterElementImportantTop && iconUrl) {
                clusterElementImportantTop.style.backgroundImage = `url("${iconUrl}")`;
              }
            } else {
              if (clusterElementTop && iconUrl) {
                clusterElementTop.style.backgroundImage = `url("${iconUrl}")`;
              }
            }
          }
        },
        rebuild: function () {
          this.build();
        },
      }
    );

    setApplicationClusterLayout(() => layout);
  };

  const [personClusterLayout, setPersonClusterLayout] = useState(null);

  const getPersonClusterLayout = (ymapsInstance: any) => {
    if (!ymapsInstance?.templateLayoutFactory) return;

    const layout = ymapsInstance.templateLayoutFactory.createClass(
      `<div>
      <div class="bottom-cluster-icon"
        style="
          position: absolute;
          left: -12px; top: -12px;
          width: 34px; height: 41px;
          opacity: 1;
          background-position: 0px 0px;
          background-size: 34px 41px;
          background-image: url(${person_cluster_map});
        "
      >
      </div>
      <div class="top-cluster-icon"
        style="
          position: absolute;
          left: -14.3px; top: -12px;
          width: 31px; height: 41px;
          opacity: 1;
          background-position: 0px 0px;
          background-size: 31px 41px;
          background-image: url();
        "
      >
      </div>
      </div>
      `,
      {
        build: function () {
          layout.superclass.build.call(this);

          const cluster = this.getData().geoObject;

          const geoObjects = cluster?.properties?._data?.geoObjects;

          if (!geoObjects || geoObjects.length === 0) return;

          const firstGeoObject = geoObjects[0];

          const matchSearchPersonStatusId = firstGeoObject?.properties?._data?.balloonContentHeader?.match(
            /data-personstatusid=["']?(\d+)["']?/
          );

          if (matchSearchPersonStatusId) {
            const clusterElementTop = this.getParentElement().querySelector('.top-cluster-icon');
            if (clusterElementTop) {
              clusterElementTop.style.backgroundImage = `url("${
                Number(matchSearchPersonStatusId[1]) === 3 ? active_person_map : person_map
              }")`;
            }
          }
        },
        rebuild: function () {
          this.build();
        },
      }
    );

    setPersonClusterLayout(() => layout);
  };

  return (
    <Spin spinning={checkIsLoading(applicationsListLoading, personsListLoading)}>
      <YMaps query={{ load: 'package.full', lang: 'ru_RU' }}>
        <YMap
          defaultState={{
            center: [55.751574, 37.573856],
            zoom: 11,
          }}
          height="80vh"
          width="98vw"
          modules={['package.full']}
          instanceRef={setMapRef}
          onLoad={(ymapsInstance) => {
            getPersonClusterLayout(ymapsInstance);
            getApplicationClusterLayout(ymapsInstance);
          }}
        >
          {personClusterLayout && applicationClusterLayout && (
            <>
              <Clusterer
                options={{
                  clusterIconLayout: applicationClusterLayout,
                  // clusterIconLayout: 'default#image',
                  // clusterIconImageHref: application_cluster_map,
                  clusterIconImageOffset: [-12, -12],
                  clusterIconColor: 'red',
                  clusterIconPieChartRadius: 12,
                  clusterIconPieChartCoreRadius: 10,
                  clusterIconPieChartStrokeWidth: 1,
                  clusterDisableClickZoom: true,
                  clusterHideIconOnBalloonOpen: false,
                  geoObjectHideIconOnBalloonOpen: false,
                  maxZoom: 21,
                  // minClusterSize: 20,
                  clusterIconShape: {
                    type: 'Rectangle',
                    coordinates: [
                      [0, 0],
                      [20, 20],
                    ],
                  },
                }}
                instanceRef={setInstanceRef}
              >
                {formatApplicationsList
                  .sort((a: any, b: any) => b?.urgent - a?.urgent)
                  .map((app) => {
                    if (app.address && app.address.coordinates) {
                      return (
                        <Placemark
                          key={app.key}
                          geometry={[app.address.coordinates.point.latitude, app.address.coordinates.point.longitude]}
                          properties={getPointDataApplication(app)}
                          options={{
                            iconLayout: 'default#image',
                            iconImageHref: importSVGIconStringForImg(
                              'app_subject',
                              'help_map',
                              'mapIconName',
                              app.subjectId,
                              app.urgent
                            ),
                            // iconImageSize: [30, 42],
                            iconImageOffset: app.iconImageOffset,
                            // balloonMinHeight: 143,
                            balloonMinWidth: 343,
                          }}
                          numbers={[10, 100]}
                          instanceRef={setPlacemarkRef}
                        />
                      );
                    }
                    return null;
                  })}
              </Clusterer>
              <Clusterer
                options={{
                  clusterIconLayout: personClusterLayout,
                  // clusterIconLayout: 'default#image',
                  // clusterIconImageHref: person_cluster_map,
                  clusterIconImageOffset: [-12, -12],
                  clusterIconPieChartRadius: 12,
                  clusterIconPieChartCoreRadius: 10,
                  clusterIconPieChartStrokeWidth: 1,
                  clusterIconColor: 'red',
                  clusterDisableClickZoom: true,
                  clusterHideIconOnBalloonOpen: false,
                  geoObjectHideIconOnBalloonOpen: false,
                  maxZoom: 21,
                  // minClusterSize: 20,
                  clusterIconShape: {
                    type: 'Rectangle',
                    coordinates: [
                      [0, 0],
                      [20, 20],
                    ],
                  },
                }}
                instanceRef={setInstanceRefPersons}
              >
                {formatPersonsList.map((person) => {
                  if (person.address && person.address.coordinates) {
                    return (
                      <Placemark
                        key={person.key}
                        geometry={[
                          person.address.coordinates.point.latitude,
                          person.address.coordinates.point.longitude,
                        ]}
                        properties={getPointDataPerson(person)}
                        options={{
                          iconLayout: 'default#image',
                          iconImageHref: person.personStatusId === 3 ? active_person_map : person_map,
                          // iconImageSize: [30, 42],
                          iconImageOffset: person.iconImageOffset,
                          // balloonMinHeight: 143,
                          balloonMinWidth: 343,
                        }}
                        numbers={[10, 100]}
                        instanceRef={setPlacemarkRef}
                      />
                    );
                  }
                  return null;
                })}
              </Clusterer>
            </>
          )}
        </YMap>
      </YMaps>
    </Spin>
  );
}

export default MapComponent;
